<template>
  <DefaultLayout :disableFacilitatorTools="true">
    <div>
      <br />
      <div class="text-center">
        <h4>{{ $t("logintitle") }}</h4>
      </div>

      <div class="text-center text-muted mb-4">
        <small>{{ $t("onlyfor") }}</small>

        <br />
        <router-link to="/">
          <small>{{ $t("participant") }}</small>
        </router-link>
      </div>

      <div class="alert alert-primary" role="alert" v-if="showLoginExpired">
        {{ $t("expired") }}
      </div>

      <small class="text-uppercase font-weight-bold">{{ $t("email") }}</small>
      <base-input
        maxLength="150"
        id="input-email"
        class="mb-3"
        v-model="form.email"
        v-bind:placeholder="$t('email')"
        v-on:keyup.native.enter="onSubmit"
      />

      <small class="text-uppercase font-weight-bold">{{ $t("password") }}</small>
      <base-input
        maxLength="50"
        type="password"
        id="input-password"
        class="mb-3"
        v-model="form.password"
        v-bind:placeholder="$t('password')"
        v-on:keyup.native.enter="onSubmit"
      />

      <div class="alert alert-primary" role="alert" v-if="alertMessage">
        {{ $t(alertMessage) }}
      </div>

      <div class="text-center">
        <base-button type="primary" v-on:click="onSubmit()">{{ $t("submit") }}</base-button>
      </div>
      <br />
      <div class="text-center">
        <router-link to="/admin/forgotpassword">
          <small>{{ $t("forgotpassword") }}</small>
        </router-link>
      </div>
      <div v-if="modalEditFacilitatorName">
        <EditFacilitatorNameDialog />
      </div>
    </div>
  </DefaultLayout>
</template>

<script>
import { FadeTransition } from "vue2-transitions";
import { getThisAdminInformation } from "@/services/APIService";
import EditFacilitatorNameDialog from "../../components/EditFacilitatorNameDialog";
import DefaultLayout from "@/layouts/DefaultLayout.vue";

export default {
  components: { EditFacilitatorNameDialog, DefaultLayout },

  data() {
    return {
      form: {
        email: "",
        password: ""
      },
      alertMessage: "",
      modalEditFacilitatorName: false,
      showLoginExpired: false
    };
  },
  created() {
    if (this.$route.params.expired) {
      this.showLoginExpired = true;
    }

    if (this.$store.getters.isLoggedIn) {
      this.$router.push("/admin/games/");
    }
  },
  mounted() {},
  methods: {
    onSubmit() {
      if (!this.form.email || !this.form.password) {
        this.alertMessage = "allfieldsrequired";
        return;
      }

      this.form.email = this.form.email.toLowerCase();

      const username = this.form.email; // Simple JWT requires to use 'username' field name
      const { password } = this.form;

      this.$store
        .dispatch("login", { username, password })
        .then(() => {
          getThisAdminInformation().then(resp => {
            if (!resp.first_name || !resp.last_name) {
              // Facilitators who have bought the game from
              // webshop for the first time don't have first_name or last_name
              // set. Let's ask them from the user.
              this.modalEditFacilitatorName = true;
            } else {
              // Other user data is set in CheckToken,
              // however let's set user role already here,
              // since it has to be in place when
              // games table is shown.
              this.$store.commit({
                type: "setUserRole",
                userRole: resp.role
              });

              this.$router.push("/admin/games");
            }
          });
        })
        .catch(err => {
          this.alertMessage = "loginfailed";
          this.form.password = "";
          this.$store.dispatch("log", err);
        });
    }
  }
};
</script>

<i18n>
{
  "en": {
    "email": "* Email address",
    "password": "* Password",
    "logintitle": "Log in",
    "onlyfor": "Only facilitators and administrators need to log in.",
    "submit": "Log in",
    "loginfailed": "Login failed",
    "participant": "If you are a player, please go back to the main screen (click here).",
    "allfieldsrequired": "Please fill in all fields before proceeding.",
    "forgotpassword": "I forgot my password",
    "expired": "Your login has expired. Please login again."
  },
  "fi": {
    "email": "* Sähköpostiosoite",
    "password": "* Salasana",
    "logintitle": "Sisäänkirjautuminen",
    "onlyfor": "Vain pelinjohtajien ja ylläpitäjien tarvitsee kirjautua sisään.",
    "submit": "Kirjaudu",
    "loginfailed": "Sisäänkirjautuminen epäonnistui",
    "participant": "Jos olet pelaaja, palaa aloitussivulle klikkaamalla tästä.",
    "allfieldsrequired": "Täytäthän kaikki kentät ennen jatkamista.",
    "forgotpassword": "Unohdin salasanani",
    "expired": "Sisäänkirjautumisesi on vanhentunut. Kirjauduthan uudestaan."
  },
 "sv": {
    "email": "* E-postadress",
    "password": "* Lösenord",
    "logintitle": "Inloggning",
    "onlyfor": "Endast spelansvariger och administratörer behöver logga in.",
    "submit": "Logga in",
    "loginfailed": "Inloggningen misslyckades",
    "participant": "Om du är en spelare, klicka här för att återgå till startsidan.",
    "allfieldsrequired": "Vänligen fyll i alla fält innan du fortsätter.",
    "forgotpassword": "Jag har glömt mitt lösenord",
    "expired": "Din inloggning har föråldrats. Logga in pånytt."
  },
 "de": {
    "email": "* E-Mail-Adresse",
    "password": "* Passwort",
    "logintitle": "Anmelden",
    "onlyfor": "Nur Spielmanager und Administratoren müssen sich anmelden.",
    "submit": "Absenden",
    "loginfailed": "Anmeldung fehlgeschlagen",
    "participant": "Wenn Sie ein Spieler sind, kehren Sie bitte zum Hauptbildschirm zurück (hier klicken).",
    "allfieldsrequired": "Bitte füllen Sie alle Felder aus, bevor Sie fortfahren.",
    "forgotpassword": "Ich habe mein Passwort vergessen",
    "expired": "Ihr Login ist abgelaufen. Bitte loggen Sie sich erneut ein."
  },
  "ko": {
    "email": "* 이메일 주소",
    "password": "* 비밀번호",
    "logintitle": "로그인",
    "onlyfor": "퍼실리테이터/관리자 모드 로그인 페이지입니다. ",
    "submit": "로그인",
    "loginfailed": "로그인에 실패하였습니다.",
    "participant": "플레이어 모드는 이곳을 눌러 메인 화면에서 로그인해주십시오.",
    "allfieldsrequired": "모든 정보를 입력해주십시오.",
    "forgotpassword": "비밀번호 찾기",
    "expired": "로그인 시간이 초과되었습니다. 다시 로그인해주십시오."
  }
}
</i18n>
